import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Helmet } from 'react-helmet-async';
import { FaNewspaper, FaChevronLeft, FaChevronRight } from 'react-icons/fa';

const DEFAULT_IMAGE = 'https://hub.mileageriot.com/assets/images/logo_sm.png';
const DEFAULT_BANNER = 'https://hub.mileageriot.com/assets/images/banner/banner.jpg';
const PAGE_SIZE = 12;

const getNewsImage = (image) => {
  if (!image) return DEFAULT_BANNER;
  if (image.includes('logo_sm.png') || image.includes('logo.svg')) return DEFAULT_BANNER;
  if (image.startsWith('http')) return image;
  return DEFAULT_BANNER;
};

const formatDate = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleDateString('ru', { day: 'numeric', month: 'short', year: 'numeric' }); } catch { return ''; }
};

const NewsCard = ({ item, index }) => {
  const img = getNewsImage(item.image);
  const isTMP = !!item.source;

  return (
    <div className="col-lg-4 col-md-6 col-12 mb-4">
      <Link to={`/news/${item.slug || item.id}`} style={{ textDecoration: 'none', display: 'block', height: '100%' }}>
        <div style={{
          background: 'linear-gradient(135deg, #1a2236 0%, #1e2b40 100%)',
          borderRadius: 16,
          overflow: 'hidden',
          height: '100%',
          display: 'flex',
          flexDirection: 'column',
          transition: 'transform 0.25s, box-shadow 0.25s',
          border: '1px solid rgba(0,203,205,0.1)',
        }}
          onMouseEnter={e => { e.currentTarget.style.transform = 'translateY(-5px)'; e.currentTarget.style.boxShadow = '0 16px 40px rgba(0,0,0,0.4)'; }}
          onMouseLeave={e => { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = 'none'; }}
        >
          {/* Image */}
          <div style={{ position: 'relative', paddingTop: '56.25%', overflow: 'hidden', flexShrink: 0 }}>
            <img
              src={img}
              alt={item.title}
              loading="lazy"
              style={{
                position: 'absolute', top: 0, left: 0, width: '100%', height: '100%',
                objectFit: 'cover', transition: 'transform 0.4s'
              }}
              onError={e => { e.target.src = DEFAULT_BANNER; }}
            />
            {/* Gradient bottom */}
            <div style={{
              position: 'absolute', bottom: 0, left: 0, right: 0,
              background: 'linear-gradient(transparent, rgba(0,0,0,0.75))',
              padding: '24px 12px 10px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end'
            }}>
              <span style={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.75rem' }}>
                {formatDate(item.created_at)}
              </span>
              {isTMP && (
                <span style={{
                  background: 'rgba(0,203,205,0.9)', color: '#fff',
                  fontSize: '0.65rem', padding: '2px 7px', borderRadius: 6,
                  fontWeight: 600
                }}>TruckersMP</span>
              )}
            </div>
            {/* MR logo */}
            <img loading="lazy" decoding="async" src={DEFAULT_IMAGE} alt="MR" style={{
              position: 'absolute', top: 10, right: 10,
              width: 24, height: 24, borderRadius: '50%',
              border: '1px solid rgba(0,203,205,0.5)',
              background: 'rgba(0,0,0,0.5)'
            }} />
          </div>

          {/* Content */}
          <div style={{ padding: '16px 18px 18px', display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
            <h5 style={{
              color: '#fff', fontSize: '0.95rem', fontWeight: 600,
              lineHeight: 1.45, marginBottom: 8,
              display: '-webkit-box', WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical', overflow: 'hidden',
              flexShrink: 0
            }}>
              {item.title}
            </h5>
            {item.description && (
              <p style={{
                color: 'rgba(255,255,255,0.5)', fontSize: '0.82rem',
                lineHeight: 1.55, margin: 0,
                display: '-webkit-box', WebkitLineClamp: 3,
                WebkitBoxOrient: 'vertical', overflow: 'hidden',
                flexGrow: 1
              }}>
                {item.description}
              </p>
            )}
            <div style={{ marginTop: 14 }}>
              <span className="btn btn-hero-primary w-100" style={{ fontSize: '0.82rem', padding: '10px 16px' }}>
                Читать далее
              </span>
            </div>
          </div>
        </div>
      </Link>
    </div>
  );
};

const News = () => {
  const [allNews, setAllNews] = useState([]);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation();
  const topRef = useRef();

  useEffect(() => {
    fetch('https://hub.mileageriot.com/api/news')
      .then(r => r.json())
      .then(d => setAllNews(d.data || []))
      .catch(console.error)
      .finally(() => setLoading(false));
  }, []);

  const totalPages = Math.ceil(allNews.length / PAGE_SIZE);
  const newsData = allNews.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const goToPage = (p) => {
    setPage(p);
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
      <Helmet>
        <title>Новости — Mileage Riot ВТК | ETS2 & ATS</title>
        <meta name="description" content="Новости виртуальной транспортной компании Mileage Riot — конвои, обновления, партнёрства, итоги месяца. ВТК ETS2 и ATS на TruckersMP." />
        <link rel="canonical" href="https://mileageriot.com/news" />
        <meta property="og:title" content="Новости — Mileage Riot ВТК" />
        <meta property="og:description" content="Новости виртуальной транспортной компании Mileage Riot." />
        <meta property="og:url" content="https://mileageriot.com/news" />
        <meta property="og:locale" content="ru_RU" />
      </Helmet>

      <section className="news-modern page-news">
        <div className="container" ref={topRef}>
          <div className="section-header text-center mb-5 fade-in-up stagger-1">
            <div className="section-badge">
              <FaNewspaper className="me-2" />
              <span>{t('news.badge') || 'Новости'}</span>
            </div>
            <h1 className="section-title text-glow text-white">{t('news.title') || 'Новости'}</h1>
            <div className="section-decoration mx-auto"></div>
            <p className="text-white-50 mt-3 section-subtitle">
              {t('news.subtitle') || 'Все события нашей компании'}
            </p>
            {!loading && allNews.length > 0 && (
              <p style={{ color: 'rgba(255,255,255,0.35)', fontSize: '0.82rem', marginTop: 4 }}>
                {allNews.length} статей · стр. {page}/{totalPages}
              </p>
            )}
          </div>

          {loading ? (
            <div className="text-center py-5">
              <div className="loading-spinner"><div className="spinner-modern"></div></div>
              <p className="mt-3 text-white-50">Загружаем новости...</p>
            </div>
          ) : (
            <>
              <div className="row">
                {newsData.map((item, i) => <NewsCard key={item.id || i} item={item} index={i} />)}
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center align-items-center flex-wrap gap-2 mt-4">
                  <button
                    className={`btn ${page === 1 ? 'btn-outline-secondary' : 'btn-outline-primary'} btn-sm`}
                    onClick={() => goToPage(page - 1)} disabled={page === 1}>
                    <FaChevronLeft className="me-1" style={{ fontSize: '0.7rem' }} /> Назад
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(p => p === 1 || p === totalPages || Math.abs(p - page) <= 2)
                    .reduce((acc, p, idx, arr) => { if (idx > 0 && arr[idx-1] !== p-1) acc.push('…'); acc.push(p); return acc; }, [])
                    .map((p, i) => p === '…' ? (
                      <span key={`d${i}`} className="text-muted px-1">…</span>
                    ) : (
                      <button key={p} onClick={() => goToPage(p)}
                        className={`btn btn-sm ${p === page ? 'btn-primary' : 'btn-outline-primary'}`}
                        style={{ minWidth: 36 }}>
                        {p}
                      </button>
                    ))}
                  <button
                    className={`btn ${page === totalPages ? 'btn-outline-secondary' : 'btn-outline-primary'} btn-sm`}
                    onClick={() => goToPage(page + 1)} disabled={page === totalPages}>
                    Вперёд <FaChevronRight className="ms-1" style={{ fontSize: '0.7rem' }} />
                  </button>
                </div>
              )}
            </>
          )}
        </div>
        <div className="news-bg-decoration"></div>
      </section>
    </>
  );
};

export default News;
